import { CANADA_PROVINCE_ABBREVIATIONS } from 'constants/CanadaProvinceTimezones';
import { US_STATE_ABBREVIATIONS } from 'constants/USStateTimezones';
import { Maybe } from 'types/UtilityTypes';

import { QuoteCountries, QuoteFormStop } from '../types';
import { parseLocation } from './location';

export const PARSED_STOP_ADDRESS_FIELD_LABELS = {
  addressLine1: 'Street Address',
  addressLine2: 'Street Address (Cont.)',
  city: 'City',
  state: 'State',
  zip: 'Zip',
  country: 'Country',
} as const;

export type ParsedStopAddressFieldKey =
  keyof typeof PARSED_STOP_ADDRESS_FIELD_LABELS;

export type ParsedStopAddress = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: QuoteCountries;
};

const US_CITY_STATE_ZIP_TRAILING =
  /^(.+),\s*([^,]+?),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i;

const US_CITY_STATE_TRAILING = /^(.+),\s*([^,]+?),\s*([A-Z]{2})$/i;

const CA_CITY_PROVINCE_POSTAL_TRAILING =
  /^(.+),\s*([^,]+?),\s*([A-Z]{2})\s+([ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ][ ]?\d[ABCEGHJKLMNPRSTVWXYZ]\d)$/i;

const US_ZIP_TRAILING = /\b(\d{5}(?:-\d{4})?|\d{4})$/;
const CA_POSTAL_TRAILING =
  /\b([ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ])\s?(\d[ABCEGHJKLMNPRSTVWXYZ]\d)$/i;
const STATE_OR_PROVINCE_TRAILING = /\b([A-Z]{2})$/i;
const STATE_OR_PROVINCE_PERIOD_BEFORE_POSTAL =
  /\b([A-Z]{2})\.+\s*(?=(?:\d{4}|\d{5}(?:-\d{4})?|[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ][ ]?\d[ABCEGHJKLMNPRSTVWXYZ]\d)$)/i;

const STREET_SUFFIXES = new Set([
  'aly',
  'alley',
  'ave',
  'avenue',
  'blvd',
  'boulevard',
  'cir',
  'circle',
  'ct',
  'court',
  'dr',
  'drive',
  'hwy',
  'highway',
  'ln',
  'lane',
  'loop',
  'pkwy',
  'parkway',
  'pl',
  'place',
  'rd',
  'road',
  'sq',
  'square',
  'st',
  'street',
  'ter',
  'terrace',
  'trl',
  'trail',
  'way',
]);

function resolveCountryFromState(state: string): QuoteCountries {
  const upper = state.toUpperCase();
  if (CANADA_PROVINCE_ABBREVIATIONS.includes(upper)) {
    return QuoteCountries.Canada;
  }

  if (US_STATE_ABBREVIATIONS.includes(upper)) {
    return QuoteCountries.USA;
  }

  return QuoteCountries.USA;
}

function splitStreetPart(streetPart: string): {
  addressLine1: string;
  addressLine2: string;
} {
  const trimmed = streetPart.trim();
  if (!trimmed) {
    return { addressLine1: '', addressLine2: '' };
  }

  const segments = trimmed
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length <= 1) {
    return { addressLine1: segments[0] ?? '', addressLine2: '' };
  }

  return {
    addressLine1: segments[0],
    addressLine2: segments.slice(1).join(', '),
  };
}

function fromSimpleLocation(
  simple: NonNullable<ReturnType<typeof parseLocation>>
): ParsedStopAddress {
  return {
    addressLine1: '',
    addressLine2: '',
    city: simple.city,
    state: simple.state,
    zip: simple.zip,
    country: simple.country,
  };
}

function fromTrailingMatch(
  streetPart: string,
  city: string,
  state: string,
  zip: string,
  country: QuoteCountries
): ParsedStopAddress {
  const street = splitStreetPart(streetPart);

  return {
    addressLine1: street.addressLine1,
    addressLine2: street.addressLine2,
    city: city.trim(),
    state: state.toUpperCase(),
    zip: zip.trim(),
    country,
  };
}

function fromPostalOnly(
  zip: string,
  country: QuoteCountries
): ParsedStopAddress {
  return {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: zip.trim(),
    country,
  };
}

function normalizeUSZip(zip: string): string {
  const trimmed = zip.trim();

  return /^\d{4}$/.test(trimmed) ? `0${trimmed}` : trimmed;
}

function normalizeHumanLocationInput(location: string): string {
  return location
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*,+\s*/g, ', ')
    .replace(/(?:,\s*){2,}/g, ', ')
    .replace(STATE_OR_PROVINCE_PERIOD_BEFORE_POSTAL, '$1 ')
    .replace(CA_POSTAL_TRAILING, (_, firstPart: string, secondPart: string) => {
      return `${firstPart.toUpperCase()} ${secondPart.toUpperCase()}`;
    });
}

function isKnownStateOrProvince(state: string): boolean {
  const upper = state.toUpperCase();
  return (
    US_STATE_ABBREVIATIONS.includes(upper) ||
    CANADA_PROVINCE_ABBREVIATIONS.includes(upper)
  );
}

function splitStreetAndCity(prefix: string): {
  streetPart: string;
  city: string;
} {
  const trimmed = prefix.trim();
  if (!trimmed) {
    return { streetPart: '', city: '' };
  }

  const commaSegments = trimmed
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (commaSegments.length > 1) {
    return {
      streetPart: commaSegments.slice(0, -1).join(', '),
      city: commaSegments[commaSegments.length - 1],
    };
  }

  const tokens = trimmed.split(' ').filter(Boolean);
  let streetSuffixIndex = -1;
  for (let index = tokens.length - 1; index >= 0; index -= 1) {
    const token = tokens[index];
    const normalizedToken = token.toLowerCase().replace(/\./g, '');
    if (STREET_SUFFIXES.has(normalizedToken)) {
      streetSuffixIndex = index;
      break;
    }
  }

  if (streetSuffixIndex === -1) {
    return { streetPart: '', city: trimmed };
  }

  const streetTokens = tokens.slice(0, streetSuffixIndex + 1);
  const cityTokens = tokens.slice(streetSuffixIndex + 1);

  return {
    streetPart: streetTokens.join(' '),
    city: cityTokens.join(' '),
  };
}

function parseTolerantLocationFromRight(
  location: string
): Maybe<ParsedStopAddress> {
  let remaining = normalizeHumanLocationInput(location);
  if (!remaining) {
    return null;
  }

  let zip = '';
  let country: QuoteCountries | undefined;

  const caPostalMatch = remaining.match(CA_POSTAL_TRAILING);
  if (caPostalMatch) {
    zip = `${caPostalMatch[1].toUpperCase()} ${caPostalMatch[2].toUpperCase()}`;
    country = QuoteCountries.Canada;
    const caPostalIndex =
      caPostalMatch.index ?? remaining.length - caPostalMatch[0].length;
    remaining = remaining.slice(0, caPostalIndex).trim();
  } else {
    const usZipMatch = remaining.match(US_ZIP_TRAILING);
    if (usZipMatch) {
      zip = normalizeUSZip(usZipMatch[1]);
      country = QuoteCountries.USA;
      const usZipIndex =
        usZipMatch.index ?? remaining.length - usZipMatch[0].length;
      remaining = remaining.slice(0, usZipIndex).trim();
    }
  }

  remaining = remaining.replace(/,\s*$/, '').trim();

  const stateMatch = remaining.match(STATE_OR_PROVINCE_TRAILING);
  if (!stateMatch) {
    if (zip && country) {
      return fromPostalOnly(zip, country);
    }

    return null;
  }

  const state = stateMatch[1].toUpperCase();
  if (!isKnownStateOrProvince(state)) {
    if (zip && country) {
      return fromPostalOnly(zip, country);
    }

    return null;
  }

  const stateIndex =
    stateMatch.index ?? remaining.length - stateMatch[0].length;
  remaining = remaining.slice(0, stateIndex).trim().replace(/,\s*$/, '');
  const split = splitStreetAndCity(remaining);
  const hasCityState = Boolean(split.city.trim()) && Boolean(state.trim());
  const hasZip = Boolean(zip.trim());

  if (!hasCityState && !hasZip) {
    return null;
  }

  return fromTrailingMatch(
    split.streetPart,
    split.city,
    state,
    zip,
    country ?? resolveCountryFromState(state)
  );
}

/**
 * Regex-based stop address parse for full-line location inputs (street + city/state/zip).
 * US/Canada handling aligns with parseLocation for the city/state/zip tail.
 */
export function parseStopAddressFromLocation(
  location: string
): Maybe<ParsedStopAddress> {
  const normalized = location.trim();
  if (!normalized) {
    return null;
  }

  const usZipMatch = normalized.match(US_CITY_STATE_ZIP_TRAILING);
  if (usZipMatch) {
    const state = usZipMatch[3];
    const country = resolveCountryFromState(state);

    return fromTrailingMatch(
      usZipMatch[1],
      usZipMatch[2],
      state,
      usZipMatch[4],
      country
    );
  }

  const caMatch = normalized.match(CA_CITY_PROVINCE_POSTAL_TRAILING);
  if (caMatch) {
    return fromTrailingMatch(
      caMatch[1],
      caMatch[2],
      caMatch[3],
      caMatch[4].toUpperCase(),
      QuoteCountries.Canada
    );
  }

  const usCityStateMatch = normalized.match(US_CITY_STATE_TRAILING);
  if (usCityStateMatch) {
    const state = usCityStateMatch[3];
    const country = resolveCountryFromState(state);

    return fromTrailingMatch(
      usCityStateMatch[1],
      usCityStateMatch[2],
      state,
      '',
      country
    );
  }

  const tolerant = parseTolerantLocationFromRight(normalized);
  if (tolerant) {
    return tolerant;
  }

  const simple = parseLocation(normalized);
  if (simple) {
    return fromSimpleLocation(simple);
  }

  return null;
}

export function formatParsedStopAddressCountry(
  country: QuoteCountries
): string {
  if (country === QuoteCountries.Canada) {
    return 'Canada';
  }

  return 'United States';
}

/** Bullet lines for tooltips; uses the same labels as parsed field metadata. */
export function getParsedStopAddressBulletLines(
  parsed: Maybe<ParsedStopAddress>
): Array<{ key: ParsedStopAddressFieldKey; label: string; value: string }> {
  if (!parsed) {
    return [];
  }

  const entries: Array<{
    key: ParsedStopAddressFieldKey;
    value: string;
  }> = [
    { key: 'addressLine1', value: parsed.addressLine1 },
    { key: 'addressLine2', value: parsed.addressLine2 },
    { key: 'city', value: parsed.city },
    { key: 'state', value: parsed.state },
    { key: 'zip', value: parsed.zip },
    {
      key: 'country',
      value: formatParsedStopAddressCountry(parsed.country),
    },
  ];

  return entries.map(({ key, value }) => ({
    key,
    label: PARSED_STOP_ADDRESS_FIELD_LABELS[key],
    value: value.trim() || '—',
  }));
}

/** True when parsed result has enough city/state/zip for LTL rating (same bar as parseLocation). */
export function isStopLocationValidForRating(location: string): boolean {
  const parsed = parseStopAddressFromLocation(location);
  if (!parsed) {
    return false;
  }

  const hasCityState =
    Boolean(parsed.city.trim()) && Boolean(parsed.state.trim());
  const hasZip = Boolean(parsed.zip.trim());

  return hasCityState || hasZip;
}

export function applyParsedStopAddressToQuoteFormStop(
  stop: QuoteFormStop,
  parsed: ParsedStopAddress
): QuoteFormStop {
  return {
    ...stop,
    addressLine1: parsed.addressLine1,
    addressLine2: parsed.addressLine2,
    city: parsed.city,
    state: parsed.state,
    zip: parsed.zip,
    country: parsed.country,
  };
}

/** Applies line-based parsing to each stop; does not replace parseLocation-only normalization. */
export function normalizeStopsFromLocationLine(
  stops: QuoteFormStop[]
): QuoteFormStop[] {
  return stops.map((stop) => {
    const parsed = parseStopAddressFromLocation(stop.location ?? '');
    if (!parsed) {
      return {
        ...stop,
        country: stop.country ?? QuoteCountries.USA,
      };
    }

    return applyParsedStopAddressToQuoteFormStop(stop, parsed);
  });
}
